import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  FiFilter,
  FiArchive,
  FiInbox,
  FiTrash2,
  FiEye,
  FiEdit,
  FiLoader,
} from 'react-icons/fi';
import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs'; // <-- Added Day.js for date manipulation

// --- Your Custom Components ---
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Table } from '../../ComponnentsUI/common/Table';

// --- Your API Hooks & Utils ---
import {
  usePaginatedPatients,
  useArchivePatient,
  useUnarchivePatient,
  useArchivePatients,
  useUnarchivePatients,
  useDeletePatient,
} from '../../../helpers/api/patients/patients';
import { handleValidDate } from './date_utils'; // Assuming this utility exists
import { NewPatient } from '../../ComponnentsUI/NewPatient/NewPatient';
import { usePermissions } from '../../../helpers/acl/permissions';
import { DateInput } from '../../ComponnentsUI/common/DateInput';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

// Define the Patient type based on your API response
interface Patient {
  id: number;
  patient_name: string;
  patient_last_name: string;
  birth_date: string;
  email: string | null;
  owner: { name: string } | null;
  insurance_provider: string | null;
  archived: boolean;
}

// --- Main Page Component ---
const PatientManagement = () => {
  const { t } = useTranslation('PatientManagement');
  useDocumentTitle(`DEEPECHO - ${t('pageTitle')}`);

  // --- State Management ---
  const [activeTab, setActiveTab] = useState<'Active' | 'Archived'>('Active');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [searchMode, setSearchMode] = useState<'AND' | 'OR'>('OR');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 7,
  });
  const [pendingOperations, setPendingOperations] = useState<Set<number>>(
    new Set()
  );
  const [isBulkOperationPending, setIsBulkOperationPending] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: '', // Expects 'YYYY-MM-DD' format
    endDate: '', // Expects 'YYYY-MM-DD' format
  });
  const navigate = useNavigate();
  // --- Permission gating (UI only; backend enforces) ---
  const { can } = usePermissions();
  const canCreate = can('patient.create');
  const canWrite = can('patient.write');
  const canArchive = can('patient.archive');
  // --- API Hooks ---
  const archivePatient = useArchivePatient();
  const unarchivePatient = useUnarchivePatient();
  const archivePatients = useArchivePatients();
  const unarchivePatients = useUnarchivePatients();
  const deletePatient = useDeletePatient();
  const queryFilters = useMemo(() => {
    // Text filters
    const textFilters: Record<string, string> = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        textFilters[`${key}__ilike`] = `%${filters[key]}%`;
      }
    });

    // Date filters
    const dateFilters: Record<string, string> = {};
    // Change 'created_at' to the actual date field in your database if different
    const DATE_FIELD_NAME = 'birth_date';
    if (dateRange.startDate) {
      dateFilters[`${DATE_FIELD_NAME}__gte`] = dayjs(dateRange.startDate)
        .startOf('day')
        .toISOString();
    }
    if (dateRange.endDate) {
      dateFilters[`${DATE_FIELD_NAME}__lte`] = dayjs(dateRange.endDate)
        .endOf('day')
        .toISOString();
    }

    // Combine all filters
    const allFilters = { ...textFilters, ...dateFilters };

    let payload: object = { condition__or: {} };
    const hasTextFilters = Object.keys(textFilters).length > 0;

    if (Object.keys(allFilters).length > 0) {
      if (searchMode === 'OR' && hasTextFilters) {
        // In OR mode, group text filters but apply date filters as AND
        payload = { condition__or: textFilters, ...dateFilters };
      } else {
        // In AND mode, all filters are at the top level
        payload = allFilters;
      }
    }

    return {
      archived: activeTab === 'Active' ? 'false' : 'true',
      filters: JSON.stringify(payload),
    };
  }, [filters, searchMode, activeTab, dateRange]);

  const patientsQuery = usePaginatedPatients(
    pagination.pageIndex + 1,
    pagination.pageSize,
    queryFilters
  );

  // Refetch data when filters change
  useEffect(() => {
    patientsQuery.refetch();
  }, [queryFilters, patientsQuery.refetch]);
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };
  const handleClearFilters = () => {
    setFilters({});
    setDateRange({ startDate: '', endDate: '' });
    // This is a simple way to clear uncontrolled inputs if needed, but controlled is better
    document
      .querySelectorAll<HTMLInputElement>(
        'input[name^="patient_"], input[name="email"]'
      )
      .forEach((input) => (input.value = ''));
  };
  // --- Handlers ---
  const debouncedFilterChange = useDebouncedCallback(
    (name: string, value: string) => {
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    350
  );

  const handleOperation = async (
    patientId: number,
    operation: 'archive' | 'unarchive' | 'delete'
  ) => {
    setPendingOperations((prev) => new Set(prev).add(patientId));

    try {
      if (operation === 'archive') await archivePatient.mutateAsync(patientId);
      if (operation === 'unarchive')
        await unarchivePatient.mutateAsync(patientId);
      if (operation === 'delete') await deletePatient.mutateAsync(patientId);

      await patientsQuery.refetch();
      if (operation === 'delete') {
        setIsDeleteModalOpen(false);
        setPatientToDelete(null);
      }
    } catch (error) {
      console.error(`Failed to ${operation} patient:`, error);
      // Add user-facing error notification here
    } finally {
      setPendingOperations((prev) => {
        const next = new Set(prev);
        next.delete(patientId);
        return next;
      });
    }
  };
  const openEditPatient = (patient: Patient) => {
    navigate(`/patients/${patient.id}/edit`, {
      state: { returnTo: '/patients' },
    });
  };
  const handleBulkAction = async (patientIds: number[]) => {
    setIsBulkOperationPending(true);
    const action = activeTab === 'Active' ? archivePatients : unarchivePatients;

    try {
      await action.mutateAsync(patientIds);
      await patientsQuery.refetch();
      table.resetRowSelection();
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsBulkOperationPending(false);
    }
  };

  const openDeleteModal = (patient: Patient) => {
    setPatientToDelete(patient);
    setIsDeleteModalOpen(true);
  };

  // --- Table Column Definitions ---
  const columns = useMemo<ColumnDef<Patient>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Input
            type="checkbox"
            className="h-4 w-4"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Input
            type="checkbox"
            className="h-4 w-4"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 20,
      },
      {
        accessorKey: 'patient_id',
        header: t('table.columns.patientId'),
        cell: (info) => (
          <div className="font-mono text-sm text-title-page-color-100 dark:text-gray-300">
            {info.getValue<string>() || '-'}
          </div>
        ),
        size: 80,
      },
      {
        accessorKey: 'patient_name',
        header: t('table.columns.firstName'),
        cell: (info) => (
          <div className="font-medium text-title-page-color-100 dark:text-white">
            {info.getValue<string>() || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'patient_last_name',
        header: t('table.columns.lastName'),
        cell: (info) => (
          <div className="text-title-page-color-100 dark:text-gray-300">
            {info.getValue<string>() || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'birth_date',
        header: t('table.columns.birthDate'),
        cell: (info) => (
          <div className="text-title-page-color-100 dark:text-gray-300">
            {handleValidDate(info.getValue())}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: t('table.columns.email'),
        cell: (info) => (
          <div className="text-title-page-color-100 dark:text-gray-300">
            {info.getValue<string>() || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'owner.name',
        header: t('table.columns.doctorInCharge'),
        cell: (info) => (
          <div className="text-title-page-color-100 dark:text-gray-300">
            {info.getValue<string>() || '-'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => (
          <div className="w-full text-center">{t('table.columns.actions')}</div>
        ),
        cell: ({ row }) => {
          const patient = row.original;
          const isPending = pendingOperations.has(patient.id);

          const iconBtnBase =
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-0 bg-transparent shadow-none transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1 hover:opacity-80 active:scale-[0.97] dark:ring-offset-slate-900';

          return (
            <div
              className="flex w-full items-center justify-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {isPending ? (
                <FiLoader className="h-6 w-6 animate-spin text-blue-500" />
              ) : (
                <>
                  <button
                    type="button"
                    className={cn(
                      iconBtnBase,
                      'text-blue-600 dark:text-blue-300'
                    )}
                    title={t('table.actions.viewProfile')}
                    aria-label={t('table.actions.viewProfile')}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/patients/${patient.id}`);
                    }}
                  >
                    <FiEye className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                  {canWrite && (
                  <button
                    type="button"
                    className={cn(
                      iconBtnBase,
                      'text-violet-600 dark:text-violet-300'
                    )}
                    title={t('table.actions.edit')}
                    aria-label={t('table.actions.edit')}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditPatient(patient);
                    }}
                  >
                    <FiEdit className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                  )}
                  {canArchive && (activeTab === 'Active' ? (
                    <button
                      type="button"
                      className={cn(
                        iconBtnBase,
                        'text-amber-700 dark:text-amber-300'
                      )}
                      title={t('table.actions.archivePatient')}
                      aria-label={t('table.actions.archivePatient')}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOperation(patient.id, 'archive');
                      }}
                    >
                      <FiArchive className="h-4 w-4" strokeWidth={2.25} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={cn(
                        iconBtnBase,
                        'text-emerald-700 dark:text-emerald-300'
                      )}
                      title={t('table.actions.unarchivePatient')}
                      aria-label={t('table.actions.unarchivePatient')}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOperation(patient.id, 'unarchive');
                      }}
                    >
                      <FiInbox className="h-4 w-4" strokeWidth={2.25} />
                    </button>
                  ))}
                  {canArchive && (
                  <button
                    type="button"
                    className={cn(
                      iconBtnBase,
                      'text-red-600 dark:text-red-400'
                    )}
                    title={t('table.actions.deletePatient')}
                    aria-label={t('table.actions.deletePatient')}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(patient);
                    }}
                  >
                    <FiTrash2 className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                  )}
                </>
              )}
            </div>
          );
        },
        size: 220,
      },
    ],
    [activeTab, pendingOperations, t, navigate, canWrite, canArchive]
  );

  // --- TanStack Table Instance ---
  const table = useReactTable({
    data: patientsQuery.data?.items ?? [],
    columns,
    rowCount: patientsQuery.data?.total_count ?? 0,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const TABS = [
    { name: 'Active', label: t('tabs.active') },
    { name: 'Archived', label: t('tabs.archived') },
  ];
  const selectedRows = table.getSelectedRowModel().rows;

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* --- Page Header --- */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
              {t('pageTitle')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('pageSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant={filtersVisible ? 'primary' : 'secondary'}
              onClick={() => setFiltersVisible(!filtersVisible)}
            >
              <FiFilter className="mr-2 h-4 w-4" /> {t('buttons.filters')}
            </Button>
            {canCreate && <NewPatient />} {/* patient.create */}
          </div>
        </div>

        {/* --- Filters Section (Collapsible) --- */}
        {filtersVisible && (
          <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex flex-wrap items-end gap-4 w-full">
              {/* Text Inputs - will grow to fill space */}
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.firstName')}
                </label>
                <Input
                  className="filter-input"
                  placeholder={t('filters.firstNamePlaceholder')}
                  name="patient_name"
                  onChange={(e) =>
                    debouncedFilterChange(e.target.name, e.target.value)
                  }
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.lastName')}
                </label>
                <Input
                  className="filter-input"
                  placeholder={t('filters.lastNamePlaceholder')}
                  name="patient_last_name"
                  onChange={(e) =>
                    debouncedFilterChange(e.target.name, e.target.value)
                  }
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.email')}
                </label>
                <Input
                  className="filter-input"
                  placeholder={t('filters.emailPlaceholder')}
                  name="email"
                  onChange={(e) =>
                    debouncedFilterChange(e.target.name, e.target.value)
                  }
                />
              </div>

              {/* Date Inputs */}
              <div className="min-w-[150px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.startDate')}
                </label>
                <DateInput
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="min-w-[150px]">
                <label className="text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1 block">
                  {t('filters.endDate')}
                </label>
                <DateInput
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  min={dateRange.startDate}
                />
              </div>

              {/* Action Buttons - pushed to the right */}
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setSearchMode((m) => (m === 'AND' ? 'OR' : 'AND'))
                  }
                >
                  {t('filters.mode')}: {searchMode}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  {t('filters.clearAll')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* --- Main Content Card (Table) --- */}
        <div className="bg-card-color-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
            <nav className="-mb-px flex space-x-6">
              {TABS.map((tab) => (
                <button
                  key={tab.name}
                  onClick={() =>
                    setActiveTab(tab.name as 'Active' | 'Archived')
                  }
                  className={cn(
                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                    activeTab === tab.name
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-title-page-color-300 dark:text-slate-400 hover:text-title-page-color-300 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
            {canArchive && selectedRows.length > 0 && (
              <div className="py-2">
                <Button
                  size="sm"
                  variant={activeTab === 'Active' ? 'secondary' : 'primary'}
                  onClick={() =>
                    handleBulkAction(selectedRows.map((row) => row.original.id))
                  }
                  disabled={isBulkOperationPending}
                >
                  {isBulkOperationPending ? (
                    <FiLoader className="animate-spin mr-2" />
                  ) : activeTab === 'Active' ? (
                    <FiArchive className="mr-2" />
                  ) : (
                    <FiInbox className="mr-2" />
                  )}
                  {activeTab === 'Active'
                    ? t('bulkActions.archive')
                    : t('bulkActions.unarchive')}{' '}
                  ({selectedRows.length})
                </Button>
              </div>
            )}
          </div>

          <Table
            table={table}
            isLoading={patientsQuery.isLoading}
            columnsLength={columns.length}
            onRowClick={(patient) => navigate(`/patients/${patient.id}`)}
          />
        </div>
      </div>

      {/* --- Delete Confirmation Modal --- */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="md"
        className="bg-card-color-200 dark:bg-gray-900"
      >
        <Modal.Header
          title={t('modals.deletePatient.title')}
          onClose={() => setIsDeleteModalOpen(false)}
        />
        <Modal.Body>
          <p className="text-sm text-title-page-color-300 dark:text-gray-300">
            {t('modals.deletePatient.message')}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            {t('modals.deletePatient.cancelButton')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => handleOperation(patientToDelete!.id, 'delete')}
          >
            {t('modals.deletePatient.confirmButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PatientManagement;
